/**
 * Hook useKanbanUnreadRealtime - Atualização de não lidas via Realtime puro
 * 
 * Cria 1 único canal Realtime por pipeline que escuta UPDATE em conversations.
 * Ao receber evento, atualiza o Map local diretamente com os dados do payload,
 * sem gerar nenhuma requisição HTTP (RPC).
 * 
 * Versão: 1.0.0 - 2026-03-07
 * 
 * Substitui o padrão anterior onde cada DealKanbanColumn criava seu próprio
 * canal Realtime que invalidava cache → disparava N re-fetches simultâneos.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTenantInboxIds, isFromCurrentTenant } from '@/hooks/useTenantInboxIds';
import { createLogger } from '@/lib/logger';
import type { UnreadSummaryData } from '@/hooks/useContactsUnreadSummary';

// Logger condicional para depuração
const logger = createLogger('KanbanUnreadRealtime');

// Tempo de batch em ms para agrupar múltiplos eventos antes de re-render
const BATCH_INTERVAL_MS = 500;

/**
 * Hook centralizado que escuta Realtime em conversations e atualiza
 * um Map<contactId, UnreadSummaryData> diretamente, sem chamadas RPC.
 * 
 * Deve ser usado no DealKanbanBoard (1 instância por pipeline).
 * O Map retornado contém apenas atualizações recebidas via Realtime.
 * As colunas mesclam este Map com os dados iniciais da RPC.
 * 
 * @param pipelineId - ID do pipeline (usado no nome do canal)
 * @param enabled - Se deve criar a subscrição Realtime
 */
export function useKanbanUnreadRealtime(
  pipelineId: string | null,
  enabled: boolean = true
) {
  // Map de atualizações Realtime: contactId → { unreadCount, lastConversationId }
  const [realtimeUpdates, setRealtimeUpdates] = useState<Map<string, UnreadSummaryData>>(new Map());

  // Ref com inbox_ids do tenant para filtro de eventos
  const inboxIdsRef = useTenantInboxIds();

  // Buffer de atualizações pendentes para batch
  const pendingUpdatesRef = useRef<Map<string, UnreadSummaryData>>(new Map());
  // Timer do batch
  const batchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Aplica atualizações pendentes ao estado em batch
   * Agrupa múltiplos eventos Realtime em um único re-render
   */
  const flushPendingUpdates = useCallback(() => {
    const pending = pendingUpdatesRef.current;
    if (pending.size === 0) return;

    logger.debug(`Aplicando ${pending.size} atualizações Realtime em batch`);

    // Cria cópia das atualizações pendentes
    const updates = new Map(pending);
    // Limpa buffer de pendentes
    pendingUpdatesRef.current = new Map();

    // Mescla com o estado atual
    setRealtimeUpdates((prev) => {
      const next = new Map(prev);
      updates.forEach((value, key) => {
        next.set(key, value);
      });
      return next;
    });
  }, []);

  useEffect(() => {
    // Só cria canal se pipeline existe e está habilitado
    if (!pipelineId || !enabled) return;

    logger.debug(`Configurando Realtime unread centralizado para pipeline ${pipelineId}`);

    // Canal único por pipeline - escuta UPDATE em conversations
    const channel = supabase
      .channel(`kanban-unread-central-${pipelineId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations',
        },
        (payload) => {
          // Filtra eventos de outros tenants via inbox_id
          if (!isFromCurrentTenant(inboxIdsRef, payload)) return;

          // Extrai dados necessários do payload
          const newData = payload.new as any;
          const contactId = newData?.contact_id;
          const unreadCount = newData?.unread_count;
          const conversationId = newData?.id;

          // Ignora se não tem contact_id (conversa sem contato vinculado)
          if (!contactId) return;

          logger.debug(`Realtime: contact=${contactId}, unread=${unreadCount}`);

          // Adiciona ao buffer de pendentes
          pendingUpdatesRef.current.set(contactId, {
            unreadCount: typeof unreadCount === 'number' ? unreadCount : 0,
            lastConversationId: conversationId || null,
          });

          // Agenda flush com debounce (agrupa eventos em rajada)
          if (batchTimerRef.current) {
            clearTimeout(batchTimerRef.current);
          }
          batchTimerRef.current = setTimeout(flushPendingUpdates, BATCH_INTERVAL_MS);
        }
      )
      .subscribe((status) => {
        logger.debug(`Realtime unread central: ${status}`);
      });

    // Cleanup: remove canal e limpa timers ao desmontar
    return () => {
      logger.debug(`Removendo Realtime unread central do pipeline ${pipelineId}`);
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current);
        batchTimerRef.current = null;
      }
      pendingUpdatesRef.current.clear();
      supabase.removeChannel(channel);
    };
  }, [pipelineId, enabled, flushPendingUpdates]);

  return {
    /** Map com atualizações Realtime de unread por contactId */
    realtimeUnreadUpdates: realtimeUpdates,
  };
}
